import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Grid, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import { DataGrid } from '@mui/x-data-grid';
import { trTR } from '@mui/x-data-grid/locales';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'src/utils/axios';
import ExcelFileCreator from 'src/components/ExcelFileCreator';

import SectionCard from 'src/components/BpoMutabakat/SectionCard';
import InfoNote from 'src/components/BpoMutabakat/InfoNote';
import LevelBadge from 'src/components/BpoMutabakat/LevelBadge';
import { getSeriesColors } from 'src/components/BpoMutabakat/colors';
import {
  formatDuration, formatHours, formatNumber, formatPercent, intervalStart
} from 'src/components/BpoMutabakat/format';
import { reportName } from 'src/components/BpoMutabakat/reports';

/** Doluluk dağılımı kovaları. Boş kova da grafikte yer alsın diye sabit liste. */
const OCCUPANCY_BUCKETS = [
  { bucket: '<40', min: -Infinity, max: 40 },
  { bucket: '40-50', min: 40, max: 50 },
  { bucket: '50-60', min: 50, max: 60 },
  { bucket: '60-70', min: 60, max: 70 },
  { bucket: '70-80', min: 70, max: 80 },
  { bucket: '80+', min: 80, max: Infinity }
];

const AgentPerformance = ({ date }) => {
  const theme = useTheme();
  const colors = getSeriesColors(theme);

  const [agents, setAgents] = useState([]);
  const [intervals, setIntervals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!date) return undefined;
    let cancelled = false;

    setLoading(true);
    setError('');

    Promise.allSettled([
      axios.get(`/GetBpoMutabakatAgents/${date}`),
      axios.get(`/GetBpoMutabakatAgentIntervals/${date}`)
    ])
      .then(([agentResult, intervalResult]) => {
        if (cancelled) return;
        setAgents(
          agentResult.status === 'fulfilled' && Array.isArray(agentResult.value?.data)
            ? agentResult.value.data
            : []
        );
        setIntervals(
          intervalResult.status === 'fulfilled' && Array.isArray(intervalResult.value?.data)
            ? intervalResult.value.data
            : []
        );
        if (agentResult.status === 'rejected' && intervalResult.status === 'rejected') {
          setError(
            agentResult.reason?.response?.status === 404
              ? 'Agent Performans servisleri henüz yayında değil.'
              : 'Veriler alınamadı. Lütfen tekrar deneyin.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [date]);

  /* Dağılım için ayrı uç yok; 474 agentin tamamı zaten elimizde. */
  const occupancyChart = useMemo(() => {
    const counts = OCCUPANCY_BUCKETS.map(
      (b) =>
        agents.filter(
          (a) => a.occupancy !== null && a.occupancy >= b.min && a.occupancy < b.max
        ).length
    );
    return {
      series: [{ name: 'Agent', data: counts }],
      options: {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          animations: { enabled: false },
          fontFamily: theme.typography.fontFamily,
          background: 'transparent'
        },
        theme: { mode: theme.palette.mode },
        colors: [colors[0]],
        plotOptions: { bar: { borderRadius: 3, columnWidth: '55%' } },
        dataLabels: { enabled: true, style: { fontSize: '11px' } },
        grid: { borderColor: theme.palette.divider, strokeDashArray: 3 },
        xaxis: {
          categories: OCCUPANCY_BUCKETS.map((b) => b.bucket),
          title: { text: 'Doluluk %', style: { fontWeight: 400, fontSize: '11px' } },
          axisTicks: { show: false }
        },
        yaxis: { labels: { formatter: (v) => formatNumber(Math.round(v)) } },
        tooltip: { theme: theme.palette.mode, y: { formatter: (v) => `${formatNumber(v)} agent` } }
      }
    };
  }, [agents, theme, colors]);

  /* --------------------------------------------- gün içi kapasite (RT 16) */
  const capacityChart = useMemo(() => ({
    series: [
      { name: 'Açık agent', type: 'column', data: intervals.map((r) => r.agents) },
      { name: 'Doluluk', type: 'line', data: intervals.map((r) => r.occupancy) }
    ],
    options: {
      chart: {
        type: 'line',
        toolbar: { show: false },
        animations: { enabled: false },
        fontFamily: theme.typography.fontFamily,
        background: 'transparent'
      },
      theme: { mode: theme.palette.mode },
      colors: [colors[0], '#2E7D32'],
      stroke: { width: [0, 2] },
      plotOptions: { bar: { borderRadius: 2, columnWidth: '65%' } },
      dataLabels: { enabled: false },
      grid: { borderColor: theme.palette.divider, strokeDashArray: 3 },
      legend: { position: 'bottom', horizontalAlign: 'left', fontSize: '12px' },
      xaxis: {
        categories: intervals.map((r) => r.slot),
        tickAmount: 12,
        labels: { rotate: 0, style: { fontSize: '11px' }, formatter: (v) => intervalStart(v) },
        axisTicks: { show: false }
      },
      yaxis: [
        {
          title: { text: 'Agent', style: { fontWeight: 400, fontSize: '11px' } },
          labels: { formatter: (v) => formatNumber(Math.round(v)) }
        },
        {
          opposite: true,
          min: 0,
          max: 100,
          title: { text: 'Doluluk', style: { fontWeight: 400, fontSize: '11px' } },
          labels: { formatter: (v) => `%${Math.round(v)}` }
        }
      ],
      tooltip: {
        theme: theme.palette.mode,
        shared: true,
        y: {
          formatter: (v, { seriesIndex }) =>
            v === null ? '—' : seriesIndex === 1 ? formatPercent(v) : formatNumber(v)
        }
      }
    }
  }), [intervals, theme, colors]);

  const columns = [
    { field: 'staffId', headerName: 'Sicil', width: 96 },
    { field: 'agent', headerName: 'Agent', flex: 1.3, minWidth: 180 },
    { field: 'team', headerName: 'Takım', flex: 1, minWidth: 130 },
    { field: 'department', headerName: 'Departman', flex: 1, minWidth: 150 },
    {
      field: 'staffedSec',
      headerName: 'Açık süre',
      type: 'number',
      width: 106,
      valueFormatter: (v) => formatHours(v)
    },
    { field: 'acdCalls', headerName: 'ACD çağrı', type: 'number', width: 106 },
    {
      field: 'aht',
      headerName: 'AHT',
      type: 'number',
      width: 90,
      valueFormatter: (v) => formatDuration(v)
    },
    { field: 'rona', headerName: 'RONA', type: 'number', width: 88 },
    {
      field: 'breakSec',
      headerName: 'Mola',
      type: 'number',
      width: 96,
      valueFormatter: (v) => formatDuration(v)
    },
    {
      field: 'lunchSec',
      headerName: 'Yemek',
      type: 'number',
      width: 96,
      valueFormatter: (v) => formatDuration(v)
    },
    { field: 'outboundCalls', headerName: 'Outbound', type: 'number', width: 106 },
    {
      field: 'occupancy',
      headerName: 'Doluluk',
      type: 'number',
      width: 124,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <LevelBadge value={params.value} />
    }
  ];

  const exportToExcel = () => {
    ExcelFileCreator(
      agents.map((a) => ({
        Sicil: a.staffId,
        Agent: a.agent,
        Takım: a.team,
        Departman: a.department,
        'Açık süre sn': a.staffedSec,
        'ACD çağrı': a.acdCalls,
        'AHT sn': a.aht,
        RONA: a.rona,
        'Mola sn': a.breakSec,
        'Yemek sn': a.lunchSec,
        Outbound: a.outboundCalls,
        'Doluluk %': a.occupancy
      })),
      `BPO Mutabakat - Agent Performans ${date}`
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <SectionCard
          title="Agent performansı"
          subtitle={`${reportName(6)} · ${formatNumber(agents.length)} agent`}
          action={
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              disabled={loading || !agents.length}
            >
              Excel
            </Button>
          }
          padded={false}
        >
          <Box sx={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={agents}
              columns={columns}
              loading={loading}
              density="compact"
              disableRowSelectionOnClick
              localeText={trTR.components.MuiDataGrid.defaultProps.localeText}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[25, 50, 100]}
              sx={{ border: 0 }}
            />
          </Box>
        </SectionCard>
      </Grid>

      <Grid size={{ xs: 12, lg: 8 }}>
        <SectionCard title="Gün içi kapasite" subtitle={`${reportName(16)} · 30 dk dilim`}>
          {loading ? (
            <Skeleton variant="rounded" height={300} />
          ) : (
            <ReactApexChart
              options={capacityChart.options}
              series={capacityChart.series}
              type="line"
              height={300}
            />
          )}
        </SectionCard>
      </Grid>

      <Grid size={{ xs: 12, lg: 4 }}>
        <SectionCard title="Doluluk dağılımı" subtitle={`${formatNumber(agents.length)} agent`}>
          {loading ? (
            <Skeleton variant="rounded" height={300} />
          ) : (
            <ReactApexChart
              options={occupancyChart.options}
              series={occupancyChart.series}
              type="bar"
              height={300}
            />
          )}
        </SectionCard>
      </Grid>

      <Grid size={12}>
        <InfoNote>
          {error ? (
            <b>{error}</b>
          ) : (
            <>
              <b>Doluluk = (görüşme + ACW + bekletme) / açık süre.</b> Mola ve yemek açık sürenin
              içindedir, bu yüzden düşük doluluk tek başına kötü performans anlamına gelmez;
              vardiya planıyla birlikte okunmalıdır. {reportName(6)} günün tamamını tek satırda
              özetler, {reportName(16)} aynı agentı gün içi dilimlere böler &mdash; mutabakat
              toplamları için birincisi, vardiya içi boşluklar için ikincisi kullanılır.
            </>
          )}
        </InfoNote>
      </Grid>
    </Grid>
  );
};

AgentPerformance.propTypes = {
  date: PropTypes.string
};

export default AgentPerformance;
